import { FC } from 'react';
import { twMerge } from 'tailwind-merge';

import { PlaceholderBar } from '../../../components/loading-skeleton/index';
import { StaggerFadeIn, StaggerItem } from '../../../components/motion/index';
import {
  capTableRootClass,
  capTableScrollAreaClass,
  capTableStickyExpandClass,
  capTableStickyHeaderClass,
  capTableStickyNameClass,
} from '../desktop-details/table-variants/cap-table-sticky-classes';

export const CapTableSelectPlaceholder: FC = () => (
  <PlaceholderBar className="h-9 w-44 animate-pulse rounded border border-gray-200 bg-gray-100" />
);

type CapTableOverAllStatusPlaceholderProps = {
  wrapperClassName?: string;
};

export const CapTableOverAllStatusPlaceholder: FC<CapTableOverAllStatusPlaceholderProps> = ({
  wrapperClassName,
}) => (
  <div
    className={twMerge(
      'flex h-full min-h-full animate-pulse flex-col items-stretch rounded-lg bg-gray-50 p-1',
      wrapperClassName,
    )}
  >
    <div className="flex w-full items-start justify-between gap-4 px-4 py-3">
      <PlaceholderBar className="h-3 w-28 rounded bg-gray-100" />
    </div>
    <div className="flex h-full w-full flex-1 flex-wrap content-start gap-3 rounded-md bg-white p-3 shadow-sm max-lg:flex-col">
      {Array.from({ length: 7 }).map((_, index) => (
        <div
          className="flex h-20 w-full flex-grow basis-[40%] flex-col gap-2 rounded-lg bg-gray-50 px-2 py-3"
          key={index}
        >
          <PlaceholderBar className="h-3 w-24 rounded bg-gray-100" />
          <PlaceholderBar className="h-5 w-32 rounded bg-gray-100" />
        </div>
      ))}
    </div>
  </div>
);

type CapTableGraphPlaceholderProps = {
  isDesktop?: boolean;
  isTablet?: boolean;
  wrapperClassName?: string;
};

export const CapTableGraphPlaceholder: FC<CapTableGraphPlaceholderProps> = ({
  wrapperClassName,
  isDesktop = false,
  isTablet = false,
}) => {
  const isMobile = !isDesktop && !isTablet;

  return (
    <div
      className={twMerge(
        'flex h-full min-h-full animate-pulse flex-col items-stretch rounded-lg bg-gray-50 p-1',
        wrapperClassName,
      )}
    >
      <div className="flex w-full items-center justify-between gap-1 px-4">
        <PlaceholderBar className="my-3 h-3 w-36 rounded bg-gray-100" />
        <PlaceholderBar className="h-8 w-36 rounded bg-gray-100" />
      </div>
      <div
        className={twMerge(
          'flex h-full min-h-0 flex-1 items-center justify-center gap-8 rounded-md bg-white px-6 py-[30px] shadow-sm',
          isMobile && 'flex-col p-3 pt-8',
          isTablet && 'px-4 py-5',
        )}
      >
        <PlaceholderBar
          className={twMerge(
            'rounded-full bg-gray-100',
            isDesktop ? 'size-[294px]' : isTablet ? 'size-[188px]' : 'size-[280px]',
          )}
        />
        <div
          className={twMerge(
            'flex max-w-[232px] flex-wrap gap-x-4 gap-y-2 pt-4',
            isMobile && 'max-w-full justify-center',
          )}
        >
          {Array.from({ length: 4 }).map((_, index) => (
            <div className="flex w-[108px] flex-col gap-2 pb-2" key={index}>
              <PlaceholderBar className="h-4 w-20 rounded bg-gray-100" />
              <PlaceholderBar className="h-3 w-14 rounded bg-gray-100" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

type CapTableDetailsPlaceholderProps = {
  className?: string;
};

export const CapTableDetailsPlaceholder: FC<CapTableDetailsPlaceholderProps> = ({ className }) => (
  <div className={twMerge('flex animate-pulse flex-col rounded-md px-4 pb-4 shadow-sm', className)}>
    <PlaceholderBar className="m-4 h-6 w-24 rounded bg-gray-100" />
    <div className="flex h-fit w-full flex-col rounded-md shadow-sm">
      <div className="flex w-full items-center justify-between p-4">
        <PlaceholderBar className="h-9 w-72 rounded border border-gray-200 bg-gray-100" />
        <div className="flex gap-3">
          <PlaceholderBar className="size-9 rounded border border-gray-100 bg-gray-100" />
          <PlaceholderBar className="h-9 w-24 rounded border border-gray-100 bg-gray-100" />
          <PlaceholderBar className="h-9 w-32 rounded border border-gray-100 bg-gray-100" />
        </div>
      </div>
      {/* Mirrors BasicTable in the default 'all' by-stakeholder view. */}
      <div className={twMerge(capTableScrollAreaClass, 'px-4 pb-4')}>
        <table className={capTableRootClass}>
          <thead className="bg-gray-50">
            <tr>
              <th className={capTableStickyExpandClass({ isHeader: true })} />
              <th
                className={twMerge(
                  'px-4 pr-6 text-left',
                  capTableStickyNameClass({ hasExpandColumn: true, isHeader: true }),
                )}
              >
                <PlaceholderBar className="h-4 w-24 rounded bg-gray-100" />
              </th>
              {Array.from({ length: 8 }).map((_, index) => (
                <th className={twMerge('px-4 pr-6 text-left', capTableStickyHeaderClass)} key={index}>
                  <PlaceholderBar className="h-4 w-16 rounded bg-gray-100" />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 6 }).map((_, index) => (
              <tr className="h-14 border-b border-gray-100" key={index}>
                <td className={capTableStickyExpandClass({ isHeader: false })}>
                  <PlaceholderBar className="size-4 rounded bg-gray-100" />
                </td>
                <td
                  className={twMerge(
                    'px-4 pr-6',
                    capTableStickyNameClass({ hasExpandColumn: true, isHeader: false }),
                  )}
                >
                  <PlaceholderBar className="h-4 w-32 rounded bg-gray-100" />
                </td>
                {Array.from({ length: 8 }).map((_cell, cellIndex) => (
                  <td className="px-4 pr-6" key={cellIndex}>
                    <PlaceholderBar className="h-4 w-12 rounded bg-gray-100" />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);

type CapTableMobileDetailsPlaceholderProps = {
  className?: string;
};

export const CapTableMobileDetailsPlaceholder: FC<CapTableMobileDetailsPlaceholderProps> = ({
  className,
}) => (
  <div
    className={twMerge(
      'flex w-full animate-pulse flex-col gap-4 rounded-md px-3 py-4 shadow-sm',
      className,
    )}
  >
    <PlaceholderBar className="h-6 w-24 rounded bg-gray-100" />
    <div className="flex flex-col overflow-hidden rounded-md shadow-sm">
      <div className="grid grid-flow-row grid-cols-2">
        {Array.from({ length: 6 }).map((_, index) => (
          <div
            className="centered flex w-full flex-col gap-3 border border-gray-100 py-6"
            key={index}
          >
            <PlaceholderBar className="h-3 w-24 rounded bg-gray-100" />
            <PlaceholderBar className="h-5 w-16 rounded bg-gray-100" />
          </div>
        ))}
      </div>
      <PlaceholderBar className="h-12 w-full rounded-none bg-brand-25/40" />
    </div>
  </div>
);

export const CapTableMobilePagePlaceholder: FC = () => (
  <div className="flex w-full animate-pulse flex-col gap-4 rounded-md bg-white px-3 py-4 shadow-sm">
    <div className="flex gap-2 pt-4">
      <PlaceholderBar className="h-3 w-16 rounded bg-gray-100" />
      <PlaceholderBar className="h-3 w-2 rounded bg-gray-100" />
      <PlaceholderBar className="h-3 w-20 rounded bg-gray-100" />
    </div>
    <div className="flex items-center justify-between border-y border-gray-100 py-3">
      <div className="flex items-center gap-2">
        <PlaceholderBar className="size-9 rounded-full bg-gray-100" />
        <div className="flex flex-col gap-2">
          <PlaceholderBar className="h-5 w-28 rounded bg-gray-100" />
          <PlaceholderBar className="h-3 w-16 rounded bg-gray-100" />
        </div>
      </div>
    </div>
    <PlaceholderBar className="h-10 w-full rounded-3xl bg-gray-100" />
    <StaggerFadeIn className="flex w-full flex-col gap-3">
      {Array.from({ length: 5 }).map((_, index) => (
        <StaggerItem key={index}>
          <div className="flex w-full flex-col gap-2 rounded-md py-3 shadow-sm">
            <div className="flex items-center gap-4 border-b-2 border-b-gray-100 px-3 pb-3">
              <PlaceholderBar className="size-10 rounded-full bg-gray-100" />
              <div className="flex flex-col gap-2">
                <PlaceholderBar className="h-5 w-36 rounded bg-gray-100" />
                <PlaceholderBar className="h-3 w-24 rounded bg-gray-100" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 px-3 pb-3">
              {Array.from({ length: 4 }).map((_, cellIndex) => (
                <PlaceholderBar className="h-10 rounded bg-gray-50" key={cellIndex} />
              ))}
            </div>
          </div>
        </StaggerItem>
      ))}
    </StaggerFadeIn>
  </div>
);
